import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, ChatCircleDots, PaperPlaneTilt, UserPlus } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || "/program";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav(nextPath);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-[#FFFFFF]">
      <div className="w-full max-w-sm">
        <Link to="/program" className="flex flex-col items-center gap-3 mb-8">
          <img src="/dftb26-mark.png" alt="" className="h-20 w-20 object-contain" />
          <img src="/dftb26-logo-dark.png" alt="DFTB26 - Back to Basics" className="h-12 w-auto object-contain" />
        </Link>

        <h1 className="font-display text-3xl mb-1 text-center">Your conference starts here</h1>
        <p className="text-sm text-[#5a5a5a] mb-6 text-center">Live programme, speaker bios, Q&amp;A and networking - all in one tap.</p>

        <ul data-testid="login-welcome-bullets" className="space-y-2 mb-6 text-sm text-[#1c1c24]">
          <li className="flex items-start gap-2">
            <Calendar size={16} weight="duotone" className="text-[#000000] mt-0.5 flex-shrink-0" />
            <span>Live programme, speaker bios and your personal agenda</span>
          </li>
          <li className="flex items-start gap-2">
            <ChatCircleDots size={16} weight="duotone" className="text-[#000000] mt-0.5 flex-shrink-0" />
            <span>Ask questions at the Ask The Experts panel and join the research quiz</span>
          </li>
          <li className="flex items-start gap-2">
            <PaperPlaneTilt size={16} weight="duotone" className="text-[#000000] mt-0.5 flex-shrink-0" />
            <span>Networking and direct messaging with other delegates</span>
          </li>
        </ul>

        {/* First-time delegate CTA - prominent so attendees don't try to sign in with
            no account and hit a confusing error. */}
        <div
          data-testid="login-first-time-card"
          className="p-4 mb-6 rounded-sm bg-[#E6F8FC] border border-[#000000]/20"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#000000] grid place-items-center">
              <UserPlus size={18} weight="bold" className="text-[#FFFFFF]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-base leading-tight text-[#1c1c24]">First time here?</div>
              <p className="text-xs text-[#5a5a5a] mt-0.5 leading-snug">
                You need to create an account first. Use the email DFTB26 has on file for you.
              </p>
            </div>
          </div>
          <Link
            to="/register"
            className="block w-full h-11 rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24] font-semibold text-center leading-[44px] transition-colors"
            data-testid="login-create-account-btn"
          >
            Create your account
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="h-px flex-1 bg-[#E5E9F0]" />
          <span className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">Already registered?</span>
          <div className="h-px flex-1 bg-[#E5E9F0]" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email" type="email" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="h-12 rounded-sm mt-1" data-testid="login-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password" type="password" required autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="h-12 rounded-sm mt-1" data-testid="login-password-input"
            />
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full h-12 rounded-sm bg-[#1c1c24] text-[#FFFFFF] hover:bg-[#000000] font-semibold">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          <div className="text-center">
            <Link to="/forgot-password" className="text-xs text-[#7A828C] hover:text-[#000000]" data-testid="login-forgot-link">
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
